import cn from 'classnames';
import { noop } from 'lodash';
import {
  type ChangeEvent,
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  type MemoExoticComponent,
  memo,
  useCallback,
} from 'react';

import { useFocus } from '@didaskill/ui/utils';

import { TEXT_MAX_LENGTH_SIZES } from '../text-max-length/constants';
import { TextMaxLength } from '../text-max-length/TextMaxLength';
import { TEXT_AREA_SIZES } from './constants';
import * as styles from './TextArea.styles';

const TEXT_AREA_MAX_LENGTH_SIZE_MAPPING = {
  [TEXT_AREA_SIZES.SMALL]: TEXT_MAX_LENGTH_SIZES.SMALL,
  [TEXT_AREA_SIZES.MEDIUM]: TEXT_MAX_LENGTH_SIZES.MEDIUM,
  [TEXT_AREA_SIZES.LARGE]: TEXT_MAX_LENGTH_SIZES.LARGE,
};

export type TextAreaProps = Omit<
  HTMLProps<HTMLTextAreaElement>,
  'onChange' | 'size' | 'value' | 'ref'
> & {
  /** Current input value. */
  value?: string;
  /** If maxLength is > 0, show the maxLength counter. */
  showMaxLength?: boolean;
  /** Text area size. */
  size?: TEXT_AREA_SIZES;
  /** Min number of lines to display without scroll bar. */
  minLines?: number;
  /** Is the textarea disabled. */
  disabled?: boolean;
  /** The textarea has an error. */
  hasError?: boolean;
  /** The textarea has a warning. */
  hasWarning?: boolean;
  /** Event handler. Called with the new value directly. */
  onChange?: (value: string, event: ChangeEvent<HTMLTextAreaElement>) => void;
  /** Use a monospace font. */
  monospace?: boolean;
};

export const TextAreaBase = forwardRef(
  (
    {
      size = TEXT_AREA_SIZES.MEDIUM,
      maxLength = null,
      showMaxLength = true,
      minLines = null,
      hasError = false,
      hasWarning = false,
      onChange = noop,
      className = null,
      monospace = false,
      ...props
    }: TextAreaProps,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const { isFocused, ...focusProps } = useFocus(props);

    const onChangeProxy = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value, event),
      [onChange],
    );

    return (
      <div css={styles.container}>
        <textarea
          {...props}
          {...focusProps}
          ref={ref}
          maxLength={maxLength}
          onChange={onChangeProxy}
          className={cn(className, size, {
            hasError,
            monospace,
            hasWarning: !hasError && !!hasWarning,
          })}
          css={[styles.textArea, minLines ? styles.minLines(minLines, size, monospace) : null]}
        />

        {!!maxLength && showMaxLength && (
          <TextMaxLength
            size={TEXT_AREA_MAX_LENGTH_SIZE_MAPPING[size]}
            maxLength={maxLength}
            currentLength={props.value?.length || 0}
            isFocused={isFocused}
            css={styles.maxLength}
          />
        )}
      </div>
    );
  },
);

TextAreaBase.displayName = 'TextArea';

export const TextArea: MemoExoticComponent<typeof TextAreaBase> & {
  SIZES?: typeof TEXT_AREA_SIZES;
  MAX_LENGTH_SIZE_MAPPING?: typeof TEXT_AREA_MAX_LENGTH_SIZE_MAPPING;
} = memo(TextAreaBase);

TextArea.SIZES = TEXT_AREA_SIZES;
TextArea.MAX_LENGTH_SIZE_MAPPING = TEXT_AREA_MAX_LENGTH_SIZE_MAPPING;
